/**
 * CatalystAuthScreen — Catalyst Embedded Auth UI (rendered only when
 * REACT_APP_AUTH_MODE === 'catalyst'). Legacy AuthScreen is untouched.
 *
 * SIGN IN  → the Catalyst login iframe via catalyst.auth.signIn(divId, config).
 *            The iframe form is themed by /app/embeddediframe.css (a vendored
 *            copy of Catalyst's default CSS + Astroite overrides) so it reads as
 *            a warm cream "sacred card" with a saffron CTA. After success
 *            Catalyst redirects to service_url → reload → App.init() → menu.
 * SIGN UP  → programmatic catalyst.auth.signUp({...}) (requires Public Signup ON);
 *            sends a verification email — the user verifies + sets a password.
 * GUEST    → unchanged: calls onGuest() (no account).
 *
 * Guard: never inject signIn() over a live session (re-check sdkSession first) —
 * that makes the SDK short-circuit into auth/signin-redirect → reload → loop.
 */
import { useEffect, useState } from 'react';
import { sdkSession, signUp } from '../auth/catalyst';
import HanumanHero from '../components/HanumanHero';
import { triggerToast } from '../components/Toast';
import { useLang } from '../i18n';

const LOGIN_DIV_ID = 'catalyst-login-container';
const CSS_VERSION = '11'; // bump when public/embeddediframe.css or fpwd.css change (Zoho caches by URL)

function haptic() { if (navigator.vibrate) navigator.vibrate(10); }

// Scoped CSS: entrance/float animations + sizing for the Catalyst-injected iframe.
const SCOPED_CSS = `
@keyframes astAuthRise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
@keyframes astAuthFade { from { opacity: 0; } to { opacity: 1; } }
.ast-auth-rise { animation: astAuthRise 0.6s cubic-bezier(0.34,1.56,0.64,1) both; }
.ast-auth-fade { animation: astAuthFade 0.7s ease 0.1s both; }
/* The login div scrolls internally (hidden scrollbar) so the form never clips. */
#${LOGIN_DIV_ID} {
  overflow-x: hidden !important;
  overflow-y: auto !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}
#${LOGIN_DIV_ID}::-webkit-scrollbar { width: 0 !important; height: 0 !important; }
/* The Catalyst SDK injects an <iframe> into the login div — fill it (Motilal pattern). */
#${LOGIN_DIV_ID} iframe {
  width: 100% !important;
  height: 100% !important;
  border: 0 !important;
  display: block !important;
  background: transparent !important;
}
@media (prefers-reduced-motion: reduce) {
  .ast-auth-rise, .ast-auth-fade { animation: none !important; }
}
`;

const S = {
  screen: {
    position: 'relative',
    minHeight: '100dvh',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 20,
    padding: 'max(28px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left))',
    background: 'radial-gradient(ellipse at 50% -5%, #4A0018 0%, #2A0010 45%, #14000A 100%)',
    overflowY: 'auto',
    isolation: 'isolate',
  },
  // Soft saffron/gold glow blobs for depth (decorative, behind content).
  glow: {
    position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
    background:
      'radial-gradient(ellipse at 50% 8%, rgba(255,107,0,0.18) 0%, transparent 45%),' +
      'radial-gradient(ellipse at 15% 80%, rgba(201,130,26,0.10) 0%, transparent 40%),' +
      'radial-gradient(ellipse at 85% 70%, rgba(139,0,0,0.18) 0%, transparent 45%)',
  },
  container: {
    position: 'relative', zIndex: 1,
    width: '100%', maxWidth: 420,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18,
  },
  title: {
    fontFamily: "'Cinzel Decorative', serif",
    fontSize: 'clamp(28px, 7.5vw, 38px)', fontWeight: 700, lineHeight: 1.1,
    background: 'linear-gradient(135deg, #F5D76E 0%, #FF8C00 50%, #D4AF37 100%)',
    WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
    textAlign: 'center', letterSpacing: 1,
    filter: 'drop-shadow(0 2px 12px rgba(255,140,0,0.35))',
  },
  subtitle: {
    fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: 15,
    color: 'rgba(245,215,110,0.78)', textAlign: 'center', marginTop: 2, letterSpacing: 0.4,
  },
  // Cream "sacred card" that frames the iframe / holds the signup form.
  card: {
    width: '100%',
    background: 'linear-gradient(180deg, #FFF6E9 0%, #FFEFD6 100%)',
    border: '1px solid rgba(201,130,26,0.45)',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 18px 50px rgba(60,20,0,0.45), 0 0 0 1px rgba(255,255,255,0.05), 0 0 34px rgba(255,107,0,0.12)',
  },
  cardPad: { padding: '22px 20px' },
  iframeBox: { width: '100%', height: 'clamp(440px, 62dvh, 520px)', display: 'block' },
  formTitle: {
    fontFamily: "'Tiro Devanagari Hindi', serif", fontSize: 22, fontWeight: 700,
    color: '#5C0010', textAlign: 'center', marginBottom: 4,
  },
  formTitleEn: {
    fontFamily: "'Mukta', sans-serif", fontSize: 12.5, fontWeight: 500,
    color: 'rgba(107,40,0,0.7)', textAlign: 'center', marginBottom: 16, letterSpacing: 0.3,
  },
  label: {
    fontFamily: "'Mukta', sans-serif", fontSize: 14, color: '#6B2800', fontWeight: 600,
    marginBottom: 6, display: 'block',
  },
  input: {
    width: '100%', padding: '14px 16px', minHeight: 54, marginBottom: 14,
    background: '#FFFDF8', border: '1.5px solid rgba(201,130,26,0.45)', borderRadius: 12,
    color: '#3A1A00', fontFamily: "'Mukta', sans-serif", fontSize: 18, outline: 'none',
    caretColor: '#FF6B00', WebkitAppearance: 'none', boxSizing: 'border-box',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
  },
  btn: {
    width: '100%', minHeight: 56, marginTop: 4,
    background: 'linear-gradient(135deg, #FF6D00 0%, #BF360C 100%)',
    border: 'none', borderRadius: 12, color: '#fff', fontFamily: "'Mukta', sans-serif",
    fontSize: 19, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 20px rgba(230,81,0,0.42)',
    touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', letterSpacing: 0.3,
  },
  // Switch link sits on the dark screen (below the cream card).
  switchLink: {
    background: 'rgba(255,237,213,0.06)', border: '1px solid rgba(201,130,26,0.32)',
    color: '#F5D76E', fontFamily: "'Mukta', sans-serif", fontSize: 15, fontWeight: 600,
    cursor: 'pointer', padding: '12px 18px', borderRadius: 14, minHeight: 48,
    textAlign: 'center', width: '100%', WebkitTapHighlightColor: 'transparent',
    backdropFilter: 'blur(6px)', transition: 'all 0.2s ease',
  },
  // In-card secondary link (e.g. "Back to sign in")
  cardLink: {
    background: 'none', border: 'none', color: '#BF360C', fontFamily: "'Mukta', sans-serif",
    fontSize: 15, fontWeight: 600, cursor: 'pointer', padding: '12px 0', display: 'block',
    textAlign: 'center', width: '100%', WebkitTapHighlightColor: 'transparent',
  },
  guestBtn: {
    background: 'none', border: 'none', color: 'rgba(255,248,231,0.5)',
    fontFamily: "'Mukta', sans-serif", fontSize: 15, cursor: 'pointer', padding: 10,
    touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
  },
  note: {
    fontFamily: "'Mukta', sans-serif", fontSize: 14, color: 'rgba(107,40,0,0.85)',
    textAlign: 'center', lineHeight: 1.65, marginBottom: 14,
  },
  msgErr: {
    fontFamily: "'Mukta', sans-serif", fontSize: 14, color: '#C41E3A',
    background: 'rgba(220,20,60,0.08)', border: '1px solid rgba(220,20,60,0.22)',
    borderRadius: 10, padding: '10px 14px', textAlign: 'center', marginBottom: 12,
  },
};

export default function CatalystAuthScreen({ onGuest }) {
  const { lang } = useLang();
  const isEn = lang === 'en';
  // Brand follows the APP's language (never browser auto-translate): English shows
  // the "RamNaam" wordmark, Hindi/Marathi show "राम नाम". translate="no" stops Chrome
  // from re-translating either (which turned राम नाम into "Name of God Ram").
  const brandName = isEn ? 'RamNaam' : 'राम नाम';
  const brandFont = isEn ? "'Cinzel Decorative', serif" : "'Tiro Devanagari Hindi', serif";
  const tagline = isEn ? 'Hanuman Ji Ki Yatra' : 'हनुमान जी की यात्रा';
  const taglineFont = isEn ? "'Mukta', sans-serif" : "'Tiro Devanagari Hindi', serif";

  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [sdkDown, setSdkDown] = useState(false);

  useEffect(() => {
    if (mode !== 'signin') return undefined;
    let cancelled = false;
    let retries = 0;
    const MAX_RETRIES = 20; // ~10s at 500ms

    const mount = async () => {
      if (cancelled) return;
      const el = document.getElementById(LOGIN_DIV_ID);
      if (!el) return;
      el.innerHTML = ''; // never double-inject (safe under re-render)

      if (window.catalyst?.auth?.signIn) {
        // Never inject over a live session — re-resolve via App on next load instead.
        const session = await sdkSession();
        if (cancelled) return; // effect torn down during the await (mode switched / unmounted)
        if (session.authenticated) { window.location.reload(); return; }
        // css_url MUST be ABSOLUTE (origin-prefixed) — a root-relative path does
        // not resolve inside the Zoho sign-in iframe, so the sheet silently fails
        // to load and the form renders raw. (Proven pattern from Motilal-Oswal.)
        const origin = window.location.origin;
        window.catalyst.auth.signIn(LOGIN_DIV_ID, {
          // Where Catalyst returns after sign-in; App.init() then resolves the session.
          service_url: `${origin}${window.location.pathname}`,
          // Theme the iframe form (vendored default CSS + Astroite overrides).
          css_url: `${origin}/app/embeddediframe.css?v=${CSS_VERSION}`,
          is_customize_forgot_password: true,
          forgot_password_id: LOGIN_DIV_ID,
          forgot_password_css_url: `${origin}/app/fpwd.css?v=${CSS_VERSION}`,
        });
      } else if (retries++ >= MAX_RETRIES) {
        setSdkDown(true);
      } else {
        setTimeout(mount, 500);
      }
    };

    Promise.resolve().then(() => requestAnimationFrame(mount));
    return () => { cancelled = true; const el = document.getElementById(LOGIN_DIV_ID); if (el) el.innerHTML = ''; };
  }, [mode]);

  function handleGuest() { haptic(); triggerToast('अतिथि के रूप में खेलें'); onGuest(); }

  return (
    <div style={S.screen}>
      <style>{SCOPED_CSS}</style>
      <div style={S.glow} aria-hidden />

      <div style={S.container} className="ast-auth-rise">
        <HanumanHero size={96} />
        <div>
          <div style={{ ...S.title, fontFamily: brandFont }} translate="no" className="notranslate">{brandName}</div>
          <div style={{ ...S.subtitle, fontFamily: taglineFont }} translate="no" className="notranslate">{tagline}</div>
        </div>

        {mode === 'signin' ? (
          sdkDown ? (
            <div style={{ ...S.card, ...S.cardPad, textAlign: 'center' }} className="ast-auth-fade">
              <div style={S.note}>
                प्रमाणीकरण सेवा लोड नहीं हुई<br />
                <span style={{ opacity: 0.7 }}>Authentication service didn’t load.</span>
              </div>
              <button style={S.btn} onClick={() => window.location.reload()}>पुनः लोड करें · Reload</button>
            </div>
          ) : (
            <>
              <div style={S.card} className="ast-auth-fade">
                <div id={LOGIN_DIV_ID} style={S.iframeBox} />
              </div>
              <button
                type="button" style={S.switchLink} onClick={() => { haptic(); setMode('signup'); }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255,237,213,0.12)'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255,237,213,0.06)'; }}
              >
                नया यात्री? खाता बनाएं&nbsp;&nbsp;·&nbsp;&nbsp;New here? Create account
              </button>
            </>
          )
        ) : (
          <div style={{ ...S.card, ...S.cardPad }} className="ast-auth-fade">
            <SignUpForm onBack={() => setMode('signin')} />
          </div>
        )}

        <button
          style={S.guestBtn} onClick={handleGuest}
          onMouseOver={(e) => { e.currentTarget.style.color = 'rgba(255,248,231,0.8)'; }}
          onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(255,248,231,0.5)'; }}
        >
          अतिथि के रूप में खेलें&nbsp;&nbsp;·&nbsp;&nbsp;Play as Guest
        </button>
      </div>
    </div>
  );
}

function SignUpForm({ onBack }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [status, setStatus] = useState({ state: 'idle', message: '' }); // idle | submitting | sent | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'submitting', message: '' });
    try {
      const res = await signUp(form);
      if (res?.status === 200) {
        haptic();
        setStatus({ state: 'sent', message: '' });
      } else {
        setStatus({ state: 'error', message: 'खाता नहीं बना — पुनः प्रयास करें · Sign-up failed, try again.' });
      }
    } catch (err) {
      setStatus({ state: 'error', message: err?.message || 'खाता नहीं बना · Sign-up failed.' });
    }
  };

  if (status.state === 'sent') {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={S.formTitle}>ईमेल भेजा गया 🙏</div>
        <div style={S.note}>
          अपना ईमेल जाँचें — सत्यापित करें और पासवर्ड बनाएं, फिर लॉगिन करें।<br />
          <span style={{ opacity: 0.75 }}>Check your inbox to verify your email and set a password, then sign in.</span>
        </div>
        <button type="button" style={S.btn} onClick={onBack}>← साइन इन पर वापस · Back to sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <div style={S.formTitle}>यात्रा शुरू करें</div>
      <div style={S.formTitleEn}>Create your account</div>

      <label style={S.label}>नाम · First name</label>
      <input style={S.input} placeholder="आपका नाम" value={form.firstName} onChange={set('firstName')} autoComplete="given-name" required />
      <label style={S.label}>उपनाम · Last name</label>
      <input style={S.input} placeholder="उपनाम" value={form.lastName} onChange={set('lastName')} autoComplete="family-name" required />
      <label style={S.label}>ईमेल · Email</label>
      <input style={S.input} type="email" placeholder="आपका ईमेल" value={form.email} onChange={set('email')} autoComplete="email" required />

      {status.state === 'error' && <div style={S.msgErr}>{status.message}</div>}
      <button type="submit" style={S.btn} disabled={status.state === 'submitting'}>
        {status.state === 'submitting' ? 'बना रहे हैं…' : 'खाता बनाएं · Create account'}
      </button>
      <button type="button" style={S.cardLink} onClick={onBack}>खाता है? साइन इन करें · Have an account? Sign in</button>
    </form>
  );
}
